import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ImageList, ImageListItem, Link, Stack, Typography } from '@mui/material';
import { ImageNotSupportedOutlined as NoPhotoIcon } from '@mui/icons-material';
import type { DeliveryEvidenceFile } from '../../types/delivery.type';

export function dedupeByAssetId(files: DeliveryEvidenceFile[]): DeliveryEvidenceFile[] {
  const byId = new Map(files.map((file) => [file.asset_id, file]));
  return Array.from(byId.values());
}

export function PhotoGallery({ title, photos }: { title: string; photos: DeliveryEvidenceFile[] }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title} ({photos.length})</Typography>
      {photos.length === 0 ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', py: 2 }}>
          <NoPhotoIcon fontSize="small" />
          <Typography variant="body2">{t('monitoring.evidence.noPhotos')}</Typography>
        </Stack>
      ) : (
        <ImageList cols={4} gap={8} sx={{ mb: 0 }}>
          {photos.map((photo) => (
            <ImageListItem key={photo.asset_id}>
              <Link href={photo.download_url ?? undefined} target="_blank" rel="noopener noreferrer">
                <Box
                  component="img"
                  src={photo.download_url ?? undefined}
                  alt={photo.filename ?? title}
                  loading="lazy"
                  sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 1, display: 'block' }}
                />
              </Link>
            </ImageListItem>
          ))}
        </ImageList>
      )}
    </Box>
  );
}

export function Detail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 1.5 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{children}</Typography>
      </Box>
    </Box>
  );
}
